import { Component } from 'react';
import styled, { css } from 'styled-components';
import { adFeedStyle, adPlainStyle } from '../../css/setting/adStyle';

type AdditionalStyle = ReturnType<typeof css>;

interface IProps {
  readonly ds: string;
  readonly parentElement: string;
  readonly typeTag?: string;
  readonly isAdWithRightColumnFeed?: boolean; // 宿り木のAdであるかのフラグ
  readonly addClass?: string; // adWrapのclassを個別に指定する
  readonly spaYadsUpdateCount?: number; // SPA対応などでyadsを動的に更新したい場合はこの値が更新される
  readonly shannonId?: string;
  readonly categoryId: string;
  readonly minHeightForPreventLayoutShift?: adMinHeightPx;
  readonly overWriteStyle?: AdditionalStyle;
  readonly isMainColumn: boolean;
  readonly isNoTopBorder: boolean;
  readonly isWithMarginBottom: boolean;
  readonly allowPrerendering?: boolean;
}

interface IStates {
  readonly isNoad: boolean;
}

export type adMinHeightPx = '250' | '200';

const Advertisement = styled.div<{ readonly $overWriteStyle?: AdditionalStyle }>`
  ${adPlainStyle}
  ${adFeedStyle}
  ${({ $overWriteStyle }) => $overWriteStyle !== undefined && $overWriteStyle}
`;

const MainColumnCss = css<{ readonly $isNoTopBorder: boolean; readonly $isWithMarginBottom: boolean }>`
  padding: 10px 0 5px;
  /* メインカラム中・ボーダーが上につく */
  margin-top: 16px;
  margin-bottom: 0;
  border-top: 1px solid ${({ theme }) => theme.news.palette.bor_lv1_pc};

  /* ボーダー無し */
  ${({ $isNoTopBorder }) =>
    $isNoTopBorder
      ? css`
          padding: 0;
          border-top: none;
        `
      : ''}
  /* コメント回遊テスト用 */
  ${({ $isWithMarginBottom }) =>
    $isWithMarginBottom
      ? css`
          margin-bottom: 16px;
        `
      : ''}
`;

const AdWrap = styled.div<{
  readonly $isNoad: boolean;
  readonly $isMainColumn: boolean;
  readonly $isAdWithRightColumnFeed: boolean;
  readonly $isNoTopBorder: boolean;
  readonly $isWithMarginBottom: boolean;
  readonly $minHeight?: adMinHeightPx;
}>`
  margin-bottom: 16px; /* 影響範囲が大きい右カラムのmargin設定をベースにする */
  ${({ $minHeight, $isNoad }) =>
    $minHeight && !$isNoad
      ? css`
          min-height: ${$minHeight}px;
        `
      : ''}
  /* メインカラムの広告用 */
  ${({ $isMainColumn }) => ($isMainColumn ? MainColumnCss : '')}
  /* サブカラムの広告用 */
  ${({ $isAdWithRightColumnFeed }) =>
    $isAdWithRightColumnFeed
      ? css`
          /* ランキング下のヤドリギなど、間の余白をなくす */
          margin-top: -16px;
        `
      : ''}
`;

/**
 * @deprecated 非同期タグを利用しないYadsNewに移行すること
 */
export default class Yads extends Component<IProps, IStates> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      isNoad: false
    };
  }
  /**
   *
   */
  public componentDidMount() {
    // noad時のcallback functionを定義
    window.newsyads = window.newsyads || {};
    window.newsyads.noadCallback = window.newsyads.noadCallback || {};
    window.newsyads.noadCallback[this.props.parentElement] = () => {
      this.setState({ isNoad: true });
    };

    if (this.props.allowPrerendering) {
      this.requestYads();
    } else if (document.prerendering) {
      document.addEventListener('prerenderingchange', () => this.requestYads(), {
        once: true
      });
    } else {
      this.requestYads();
    }
  }

  // spaに対応する場合spaYadsUpdateCountの値が更新されたら広告を再引き当てする
  /**
   * @param nextProps
   * @param nextState
   */
  public shouldComponentUpdate(nextProps: IProps, nextState: IStates) {
    if (this.state.isNoad !== nextState.isNoad) {
      return true;
    }
    return nextProps.spaYadsUpdateCount !== undefined && this.props.spaYadsUpdateCount !== nextProps.spaYadsUpdateCount;
  }
  /**
   *
   */
  public componentDidUpdate() {
    if (!this.state.isNoad) {
      this.requestYads();
    }
  }
  /**
   *
   */
  public render() {
    return (
      <AdWrap
        $minHeight={this.props.minHeightForPreventLayoutShift}
        $isNoad={this.state.isNoad}
        $isMainColumn={this.props.isMainColumn}
        $isAdWithRightColumnFeed={this.props.isAdWithRightColumnFeed ?? false}
        $isNoTopBorder={this.props.isNoTopBorder}
        $isWithMarginBottom={this.props.isWithMarginBottom}
        className={this.props.addClass ? this.props.addClass : ''}
      >
        <Advertisement id={this.props.parentElement} $overWriteStyle={this.props.overWriteStyle} />
      </AdWrap>
    );
  }

  private requestYads() {
    if (!window.yadsRequestAsync) {
      return;
    }

    let params: {
      yads_ad_ds: string;
      yads_parent_element: string;
      yads_bucket_id: string;
      yads_type_tag: string;
      yads_shannon_id?: string;
      yads_placement_category_id: string;
      yads_noad_callback?: string;
    } = {
      yads_ad_ds: this.props.ds,
      yads_parent_element: this.props.parentElement,
      yads_bucket_id: window.miffy_bucket_id || '',
      yads_type_tag: window.miffy_type_tag || this.props.typeTag || '',
      yads_placement_category_id: this.props.categoryId,
      yads_noad_callback: `window.newsyads.noadCallback.${this.props.parentElement}`
    };
    if (this.props.shannonId) {
      params = {
        ...params,
        yads_shannon_id: this.props.shannonId
      };
    }
    window.yadsRequestAsync(params);
  }
}
